import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  Tabs,
  Tab,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Collapse,
  Card,
  CardContent,
  useTheme,
  alpha,
  Avatar
} from '@mui/material';
import {
  Close,
  Person,
  Schedule,
  Business,
  Info,
  Code,
  SwapHoriz,
  ExpandMore,
  ExpandLess,
  ContentCopy,
  CheckCircle,
  ErrorOutline
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ParsedAuditLog, AuditTimelineItem } from '../../types/Audit.types';
import { AuditService } from '../../services/audit-service';

interface AuditLogDialogProps {
  open: boolean;
  onClose: () => void;
  log?: ParsedAuditLog | null;
  timelineItem?: AuditTimelineItem | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

const AuditLogDialog: React.FC<AuditLogDialogProps> = ({
  open,
  onClose,
  log,
  timelineItem
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']));
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const data = log || timelineItem;
  if (!data) return null;

  const isTimelineItem = !log && timelineItem;
  const actionColor = AuditService.getActionColor(data.action);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const safeStringify = (value: any): string => {
    if (value === null || value === undefined) return '';
    
    if (typeof value === 'object' && value._owner) {
      return '[React Component]';
    }
    
    if (typeof value === 'object' && value.nodeType) {
      return '[DOM Node]';
    }

    try {
      if (typeof value === 'object') {
        const cleanValue = JSON.parse(JSON.stringify(value, (key, val) => {
          if (key.startsWith('_') || key.includes('Fiber') || key.includes('react')) {
            return undefined;
          }
          if (typeof val === 'object' && val !== null) {
            if (val.nodeType || val._owner || val.stateNode) {
              return '[Object]';
            }
          }
          return val;
        }));
        return JSON.stringify(cleanValue, null, 2);
      }
      return String(value);
    } catch (error) {
      return String(value);
    }
  };

  const renderInfoCard = (title: string, children: React.ReactNode, collapsible = false, defaultExpanded = true) => {
    const sectionKey = title.toLowerCase().replace(/\s+/g, '-');
    const isExpanded = expandedSections.has(sectionKey);
    
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <Box
          sx={{
            p: 2,
            cursor: collapsible ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(theme.palette.primary.main, 0.05)
          }}
          onClick={() => collapsible && toggleSection(sectionKey)}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {collapsible && (
            <IconButton size="small">
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>
        <Collapse in={!collapsible || isExpanded}>
          <Box sx={{ p: 2 }}>
            {children}
          </Box>
        </Collapse>
      </Card>
    );
  };

  const renderField = (label: string, value: any, copyable = false, monospace = false) => {
    if (value === null || value === undefined) return null;
    
    const displayValue = safeStringify(value);
    const fieldKey = `${label}-${Date.now()}`;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ minWidth: 120, fontWeight: 500, pt: 0.25 }}
          >
            {label}:
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: monospace ? 'monospace' : 'inherit',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap'
              }}
            >
              {displayValue}
            </Typography>
          </Box>
          {copyable && (
            <IconButton
              size="small"
              onClick={() => copyToClipboard(displayValue, fieldKey)}
              sx={{ ml: 1 }}
            >
              {copiedField === fieldKey ? (
                <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
              ) : (
                <ContentCopy sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          )}
        </Stack>
      </Box>
    );
  };

  const renderBasicInfo = () => (
    renderInfoCard("Basic Information", (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Avatar sx={{ width: 40, height: 40, bgcolor: `${actionColor}.main` }}>
                <Person sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {isTimelineItem ? timelineItem.user.name : log.userName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isTimelineItem ? 'Timeline User' : 'Audit User'}
                </Typography>
              </Box>
            </Stack>
          </Box>
          
          {renderField("Action", (
            <Chip 
              label={typeof data.action === 'number' ? 
                Object.entries({1: 'Create', 2: 'Update', 3: 'Delete', 5: 'Receive', 10: 'Bulk Operation'})[data.action - 1]?.[1] || `Action ${data.action}` :
                data.action
              }
              color={actionColor}
              size="small"
            />
          ))}
          
          {renderField("Entity Type", log ? log.entityType : (isTimelineItem ? 'Timeline' : 'Unknown'))}
          {renderField("Entity ID", isTimelineItem ? 'N/A' : log.entityId, true, true)}
          {renderField("Description", isTimelineItem ? timelineItem.description : log.description)}
        </Grid>
        
        <Grid item xs={12} md={6}>
          {renderField("Timestamp", format(
            isTimelineItem ? timelineItem.timestamp : log.parsedTimestamp,
            'PPpp'
          ))}
          {renderField("User ID", isTimelineItem ? timelineItem.user.id : log.userId, true, true)}
          {!isTimelineItem && renderField("Log ID", log.id, true, true)}
        </Grid>
      </Grid>
    ))
  );

  const renderChanges = () => {
    const changes = isTimelineItem ? timelineItem.changes : null;
    const hasOldNew = log && (log.oldValues || log.newValues);
    
    if (!changes && !hasOldNew) return null;

    return renderInfoCard("Changes", (
      <>
        {changes && changes.length > 0 && (
          <Stack spacing={2}>
            {changes.map((change, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {change.field}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Previous Value
                    </Typography>
                    <Paper 
                      sx={{ 
                        p: 1.5, 
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                      }}
                    >
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                        {change.oldValue === null || change.oldValue === undefined ? 
                          <em style={{ color: theme.palette.text.secondary }}>empty</em> : 
                          safeStringify(change.oldValue)
                        }
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SwapHoriz color="primary" />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      New Value
                    </Typography>
                    <Paper 
                      sx={{ 
                        p: 1.5, 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                      }}
                    >
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                        {change.newValue === null || change.newValue === undefined ? 
                          <em style={{ color: theme.palette.text.secondary }}>empty</em> : 
                          safeStringify(change.newValue)
                        }
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}

        {hasOldNew && (
          <Box sx={{ mt: changes?.length ? 3 : 0 }}>
            <Grid container spacing={2}>
              {log.oldValues && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Old Values
                  </Typography>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      bgcolor: alpha(theme.palette.error.main, 0.05),
                      maxHeight: 300,
                      overflow: 'auto'
                    }}
                  >
                    <pre style={{ 
                      margin: 0, 
                      fontFamily: 'monospace', 
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {safeStringify(log.oldValues)}
                    </pre>
                  </Paper>
                </Grid>
              )}
              
              {log.newValues && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    New Values
                  </Typography>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      bgcolor: alpha(theme.palette.success.main, 0.05),
                      maxHeight: 300,
                      overflow: 'auto'
                    }}
                  >
                    <pre style={{ 
                      margin: 0, 
                      fontFamily: 'monospace', 
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {safeStringify(log.newValues)}
                    </pre>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </>
    ), true, false);
  };

  const renderMetadata = () => {
    const metadata = data.metadata;
    if (!metadata) return null;

    return renderInfoCard("Additional Information", (
      <Stack spacing={3}>
        {metadata.TriggerType && (
          <Box>
            <Chip 
              label={`Triggered by: ${metadata.TriggerType}`}
              color="info"
              variant="outlined"
              icon={<Info />}
            />
          </Box>
        )}

        {metadata.InfoData && Object.keys(metadata.InfoData).length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Information Data
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2}>
                {Object.entries(metadata.InfoData).map(([key, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {key}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                        {safeStringify(value)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
        )}

        {metadata.RelationData && Object.keys(metadata.RelationData).length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Related Entities
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                {Object.entries(metadata.RelationData).map(([key, value]) => (
                  <Stack key={key} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {key}:
                    </Typography>
                    <Chip 
                      label={safeStringify(value)}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Box>
        )}
      </Stack>
    ), true, false);
  };

  const renderRawData = () => (
    renderInfoCard("Raw Data", (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Complete raw data for debugging purposes
        </Typography>
        <Paper 
          sx={{ 
            p: 2, 
            bgcolor: theme.palette.grey[50],
            maxHeight: 400,
            overflow: 'auto'
          }}
        >
          <pre style={{ 
            margin: 0, 
            fontFamily: 'monospace', 
            fontSize: '0.75rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {safeStringify(data)}
          </pre>
        </Paper>
      </Box>
    ), true, false)
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {isTimelineItem ? 'Timeline Event Details' : 'Audit Log Details'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isTimelineItem ? timelineItem.title : `${log?.entityType ?? ''} ${data.action}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ ml: 2 }}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="audit log tabs"
          >
            <Tab label="Overview" />
            <Tab label="Changes" disabled={!renderChanges()} />
            <Tab label="Metadata" disabled={!data.metadata} />
            <Tab label="Raw Data" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          <TabPanel value={activeTab} index={0}>
            {renderBasicInfo()}
          </TabPanel>
          
          <TabPanel value={activeTab} index={1}>
            {renderChanges()}
          </TabPanel>
          
          <TabPanel value={activeTab} index={2}>
            {renderMetadata()}
          </TabPanel>
          
          <TabPanel value={activeTab} index={3}>
            {renderRawData()}
          </TabPanel>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogDialog;